<template>
  <div class="page-container">
    <div class="page-header">
      <h2>聚合链接管理</h2>
      <div class="header-actions">
        <el-button 
          v-if="isAdmin" 
          type="info" 
          @click="$router.push('/users')"
          style="margin-right: 10px;"
        >
          <el-icon><User /></el-icon>
          用户管理
        </el-button>
        <el-button type="primary" @click="showCreateDialog = true">
          <el-icon><Plus /></el-icon>
          新建聚合链接
        </el-button>
      </div>
    </div>

    <!-- 聚合链接列表 -->
    <el-table 
      :data="aggregatorList" 
      v-loading="loading" 
      stripe
      class="modern-table"
      :header-cell-style="{ background: '#f8f9ff', color: '#606266', fontWeight: '600' }"
    >
      <el-table-column prop="id" label="ID" width="80" align="center">
        <template #default="{ row }">
          <el-tag type="info" size="small">{{ row.id }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="名称" min-width="180">
        <template #default="{ row }">
          <div class="name-cell">
            <el-icon><Link /></el-icon>
            <span>{{ row.name }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" min-width="250" show-overflow-tooltip>
        <template #default="{ row }">
          <div class="description-cell">
            {{ row.description || '暂无描述' }}
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="deeplink_count" label="DeepLink数量" width="140" align="center">
        <template #default="{ row }">
          <el-badge :value="row.deeplink_count || 0" type="primary" class="count-badge">
            <el-icon><Connection /></el-icon>
          </el-badge>
        </template>
      </el-table-column>
      <el-table-column prop="total_clicks" label="总点击量" width="140" align="center">
        <template #default="{ row }">
          <div class="click-count">
            <el-icon><View /></el-icon>
            <span>{{ row.total_clicks || 0 }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="redirect_mode" label="跳转方案" width="140" align="center">
        <template #default="{ row }">
          <el-tag 
            :type="getRedirectModeType(row.redirect_mode)" 
            size="large"
            effect="light"
          >
            <el-icon>
              <VideoPlay v-if="row.redirect_mode === 'youku_popup'" />
              <Iphone v-else-if="row.redirect_mode === 'ios_direct'" />
              <Monitor v-else-if="row.redirect_mode === 'android_direct'" />
              <VideoPlay v-else-if="row.redirect_mode === 'universal_redirect'" />
              <VideoPlay v-else />
            </el-icon>
            {{ getRedirectModeText(row.redirect_mode) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="120" align="center">
        <template #default="{ row }">
          <el-tag 
            :type="row.status === 'active' ? 'success' : 'danger'" 
            size="large"
            effect="light"
          >
            <el-icon>
              <CircleCheck v-if="row.status === 'active'" />
              <CircleClose v-else />
            </el-icon>
            {{ row.status === 'active' ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="200" align="center">
        <template #default="{ row }">
          <div class="time-cell">
            <el-icon><Calendar /></el-icon>
            <span>{{ formatDate(row.created_at) }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="280" fixed="right" align="center">
        <template #default="{ row }">
          <div class="action-buttons">
            <el-button size="small" @click="viewDetail(row.id)" class="action-btn">
              <el-icon><View /></el-icon>
              详情
            </el-button>
            <el-button size="small" type="primary" @click="editAggregator(row)" class="action-btn">
              <el-icon><Edit /></el-icon>
              编辑
            </el-button>
            <el-button size="small" type="info" @click="viewStats(row.id)" class="action-btn">
              <el-icon><DataAnalysis /></el-icon>
              统计
            </el-button>
            <el-button size="small" type="danger" @click="deleteAggregator(row)" class="action-btn">
              <el-icon><Delete /></el-icon>
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 创建/编辑对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      v-model="showCreateDialog" 
      width="600px"
      @close="resetForm"
      class="modern-dialog"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px" class="modern-form">
        <el-form-item label="名称" prop="name">
          <el-input 
            v-model="form.name" 
            placeholder="请输入聚合链接名称"
            size="large"
            prefix-icon="Edit"
          />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="4"
            placeholder="请输入描述信息"
            resize="none"
          />
        </el-form-item>
        <el-form-item label="跳转方案" prop="redirect_mode">
          <el-radio-group v-model="form.redirect_mode" size="large">
            <el-radio label="youku_popup">
              <el-icon><VideoPlay /></el-icon>
              优酷弹窗
            </el-radio>
            <el-radio label="ios_direct">
              <el-icon><Iphone /></el-icon>
              iOS直接跳转
            </el-radio>
            <el-radio label="android_direct">
              <el-icon><Monitor /></el-icon>
              Android直接跳转
            </el-radio>
            <el-radio label="universal_redirect">
              <el-icon><VideoPlay /></el-icon>
              通用跳转
            </el-radio>
          </el-radio-group>
          <div class="form-tip">
            <el-icon><InfoFilled /></el-icon>
            <span>优酷弹窗：显示确认弹窗后跳转；直接跳转：页面加载后自动跳转；通用跳转：复制优酷弹窗方式</span>
          </div>
        </el-form-item>
        <el-form-item label="状态" prop="status" v-if="isEdit">
          <el-radio-group v-model="form.status" size="large">
            <el-radio label="active">
              <el-icon><CircleCheck /></el-icon>
              启用
            </el-radio>
            <el-radio label="inactive">
              <el-icon><CircleClose /></el-icon>
              禁用
            </el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button size="large" @click="showCreateDialog = false">取消</el-button>
          <el-button type="primary" size="large" @click="submitForm">
            <el-icon><Check /></el-icon>
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Link, Connection, View, Edit, Delete, CopyDocument, DataAnalysis, User, VideoPlay, Iphone, Monitor, InfoFilled } from '@element-plus/icons-vue'
import { aggregatorApi } from '../api'
import { auth } from '../utils/auth'

export default {
  name: 'AggregatorList',
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const aggregatorList = ref([])
    const showCreateDialog = ref(false)
    const isEdit = ref(false)
    const formRef = ref()
    
    // 检查是否为管理员
    const isAdmin = computed(() => {
      const user = auth.getCurrentUser()
      return user && user.role === 'admin'
    })
    
    const form = reactive({
      id: null,
      name: '',
      description: '',
      redirect_mode: 'youku_popup',
      status: 'active'
    })
    
    const rules = {
      name: [
        { required: true, message: '请输入名称', trigger: 'blur' }
      ]
    }
    
    const dialogTitle = computed(() => isEdit.value ? '编辑聚合链接' : '新建聚合链接')
    
    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '未知'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    // 获取跳转方案文本
    const getRedirectModeText = (mode) => {
      const modeMap = {
        'youku_popup': '优酷弹窗',
        'ios_direct': 'iOS直接',
        'android_direct': 'Android直接',
        'universal_redirect': '通用跳转'
      }
      return modeMap[mode] || '优酷弹窗'
    }
    
    // 获取跳转方案标签类型
    const getRedirectModeType = (mode) => {
      const typeMap = {
        'youku_popup': 'primary',
        'ios_direct': 'success',
        'android_direct': 'warning',
        'universal_redirect': 'info'
      }
      return typeMap[mode] || 'primary'
    }
    
    // 获取聚合链接列表
    const getAggregatorList = async () => {
      try {
        loading.value = true
        const response = await aggregatorApi.getList()
        if (response.success) {
          aggregatorList.value = response.data
        }
      } catch (error) {
        ElMessage.error('获取聚合链接列表失败')
      } finally {
        loading.value = false
      }
    }
    
    // 查看详情
    const viewDetail = (id) => {
      router.push(`/aggregator/${id}`)
    }
    
    // 查看统计
    const viewStats = (id) => {
      router.push(`/statistics/${id}`)
    }
    
    // 编辑聚合链接
    const editAggregator = (row) => {
      isEdit.value = true
      Object.assign(form, row)
      showCreateDialog.value = true
    }
    
    // 删除聚合链接
    const deleteAggregator = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除聚合链接"${row.name}"吗？此操作不可恢复！`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const response = await aggregatorApi.delete(row.id)
        if (response.success) {
          ElMessage.success('删除成功')
          getAggregatorList()
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
    
    // 提交表单
    const submitForm = async () => {
      try {
        await formRef.value.validate()
        
        let response
        if (isEdit.value) {
          response = await aggregatorApi.update(form.id, form)
        } else {
          response = await aggregatorApi.create(form)
        }
        
        if (response.success) {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          showCreateDialog.value = false
          getAggregatorList()
        }
      } catch (error) {
        ElMessage.error('操作失败')
      }
    }
    
    // 重置表单
    const resetForm = () => {
      isEdit.value = false
      Object.assign(form, {
        id: null,
        name: '',
        description: '',
        redirect_mode: 'youku_popup',
        status: 'active'
      })
      formRef.value?.resetFields()
    }
    
    onMounted(() => {
      getAggregatorList()
    })
    
    return {
      loading,
      aggregatorList,
      showCreateDialog,
      isEdit,
      form,
      rules,
      formRef,
      dialogTitle,
      isAdmin,
      formatDate,
      getRedirectModeText,
      getRedirectModeType,
      getAggregatorList,
      viewDetail,
      viewStats,
      editAggregator,
      deleteAggregator,
      submitForm,
      resetForm
    }
  }
}
</script>

<style scoped>
/* 基础样式 */
.page-container {
  background: white;
  border-radius: 0;
  padding: 20px;
  box-shadow: none;
  backdrop-filter: none;
  border: none;
  width: 100%;
  margin: 0;
  min-height: calc(100vh - 120px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f2f5;
}

.header-actions {
  display: flex;
  align-items: center;
}

.page-header h2 {
  margin: 0;
  color: #303133;
  font-size: 28px;
  font-weight: 600;
}

.modern-dialog {
  border-radius: 16px;
}

.modern-form {
  padding: 20px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #f0f2f5;
}

/* 表格样式优化 */
.modern-table {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

:deep(.el-table) {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.el-table__header) {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2f5 100%);
}

:deep(.el-table th) {
  background: transparent;
  color: #606266;
  font-weight: 600;
  border-bottom: 2px solid #e4e7ed;
  padding: 16px 12px;
  font-size: 14px;
}

:deep(.el-table td) {
  border-bottom: 1px solid #f0f2f5;
  padding: 16px 12px;
  vertical-align: middle;
}

:deep(.el-table tr:hover > td) {
  background-color: #f8f9ff;
  transform: scale(1.01);
  transition: all 0.3s ease;
}

/* 表格单元格样式 */
.name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #303133;
}

.name-cell .el-icon {
  color: #409EFF;
  font-size: 16px;
}

.description-cell {
  color: #606266;
  line-height: 1.5;
  max-width: 200px;
}

.count-badge {
  display: flex;
  align-items: center;
  justify-content: center;
}

.count-badge .el-icon {
  font-size: 18px;
  color: #409EFF;
}

.click-count {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 500;
  color: #606266;
}

.click-count .el-icon {
  color: #67C23A;
  font-size: 16px;
}

.time-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #909399;
  font-size: 13px;
}

.time-cell .el-icon {
  color: #909399;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.action-btn {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
}

/* 按钮样式优化 */
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

:deep(.el-button:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
}

/* 标签样式优化 */
:deep(.el-tag) {
  border-radius: 6px;
  font-weight: 500;
}

/* 电脑端优化 */
@media (min-width: 1200px) {
  .page-container {
    width: 100%;
    padding: 20px;
    min-height: calc(100vh - 120px);
  }
  
  .page-header h2 {
    font-size: 32px;
  }
  
  .modern-table {
    font-size: 15px;
  }
  
  :deep(.el-table th) {
    padding: 20px 16px;
    font-size: 15px;
  }
  
  :deep(.el-table td) {
    padding: 20px 16px;
  }
  
  .action-buttons {
    gap: 10px;
  }
  
  .action-btn {
    padding: 8px 16px;
    font-size: 14px;
  }
}

/* 平板端优化 */
@media (min-width: 769px) and (max-width: 1199px) {
  .page-container {
    width: 100%;
    padding: 25px;
    min-height: calc(100vh - 120px);
  }
  
  .page-header h2 {
    font-size: 28px;
  }
  
  .action-buttons {
    gap: 6px;
  }
  
  .action-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
}

/* 表单提示样式 */
.form-tip {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  font-size: 13px;
  color: #0369a1;
}

.form-tip .el-icon {
  margin-right: 6px;
  font-size: 14px;
}

/* 移动端响应式设计 */
@media (max-width: 768px) {
  .page-container {
    width: 100%;
    padding: 15px;
    min-height: calc(100vh - 120px);
  }
  
  .page-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .page-header h2 {
    font-size: 24px;
  }
  
  .modern-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
  
  .action-btn {
    width: 100%;
    justify-content: center;
  }
  
  :deep(.el-table th),
  :deep(.el-table td) {
    padding: 12px 8px;
    font-size: 13px;
  }
}
</style>
